import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Divider,
  Drawer,
  Stack,
  Typography,
  CircularProgress,
  LinearProgress,
  IconButton,
  useMediaQuery,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import KeyboardArrowLeftRoundedIcon from '@mui/icons-material/KeyboardArrowLeftRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import MapIcon from '@mui/icons-material/Map';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { PlannerExperience } from '../../types/planner';
import { alpha, useTheme } from '@mui/material/styles';
import { emphasizeMarkdownLead } from '../../utils/markdown';
import { MarkdownText } from '../common/MarkdownText';
import { createLogger } from '../../utils/logger';
import { textScale } from '../../theme/standards';


const log = createLogger('ExperienceDetailDrawer');

const normalizeImageSrc = (src: string): string => {
  const trimmed = (src || '').trim();
  if (!trimmed) return '';

  if (trimmed.startsWith('data:')) {
    return trimmed;
  }

  try {
    let candidate = trimmed;
    if (candidate.startsWith('//')) {
      candidate = `https:${candidate}`;
    }

    if (/^https?:/i.test(candidate)) {
      const parsed = new URL(candidate);
      return `${parsed.protocol}//${parsed.host}${parsed.pathname}`.toLowerCase();
    }
  } catch (error) {
    // Ignore parse issues and fall back to simple normalization
  }

  const base = trimmed.split('?')[0];
  return base.toLowerCase();
};


interface ActivityDetails {
  name: string;
  description: string;
  historical_significance: string;
  cultural_context: string;
  visitor_experience: string;
  insider_tips: string[];
  seasonal_considerations: string;
  interesting_facts: string[];
  local_perspective: string;
  address: string;
  duration?: string;
  cost?: string;
  working_hours?: string;
}

interface ExperienceDetailDrawerProps {
  experience: PlannerExperience | null;
  open: boolean;
  onClose: () => void;
  destination?: string;
  onOpenExperience?: (experience: PlannerExperience) => void;
  onDelete?: (id: string) => void;
  isDeleting?: boolean;
}

export const ExperienceDetailDrawer: React.FC<ExperienceDetailDrawerProps> = ({ experience, open, onClose, destination, onOpenExperience, onDelete, isDeleting }) => {
  // Store state keyed by activity ID to support multiple concurrent requests
  const [enrichedDetailsMap, setEnrichedDetailsMap] = useState<Record<string, ActivityDetails>>({});
  const [loadingMap, setLoadingMap] = useState<Record<string, boolean>>({});
  const [errorMap, setErrorMap] = useState<Record<string, string>>({});
  const [successNotifications, setSuccessNotifications] = useState<Record<string, boolean>>({});
  const [experienceMap, setExperienceMap] = useState<Record<string, PlannerExperience>>({});
  const [lastExperience, setLastExperience] = useState<PlannerExperience | null>(null);
  const [heroFailedUrls, setHeroFailedUrls] = useState<Record<string, boolean>>({});
  const [heroImageIndex, setHeroImageIndex] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const accentColor = theme.palette.secondary.main;
  const heroGradient = `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.92)} 0%, ${alpha(accentColor, 0.82)} 100%)`;

  // Update lastExperience and experienceMap when a new experience is provided
  // Also update if the experience object reference changes (e.g., when photos load)
  useEffect(() => {
    if (experience) {
      setLastExperience(experience);
      setExperienceMap(prev => {
        const existing = prev[experience.id];
        // Only update if it's a new experience or if the data has changed (like photos loading)
        if (!existing || existing.imageUrl !== experience.imageUrl ||
            JSON.stringify(existing.imageUrls) !== JSON.stringify(experience.imageUrls)) {
          return { ...prev, [experience.id]: experience };
        }
        return prev;
      });
    }
  }, [experience, experience?.imageUrl, experience?.imageUrls]);

  // Use lastExperience if experience is null (drawer closed but we want to keep notifications)
  const currentExperience = experience || lastExperience;

  const heroImageSources = React.useMemo(() => {
    if (!currentExperience) {
      return [] as string[];
    }

    const combined = [
      ...(currentExperience.imageUrl ? [currentExperience.imageUrl] : []),
      ...(Array.isArray(currentExperience.imageUrls) ? currentExperience.imageUrls : []),
    ];

    const seen = new Set<string>();
    return combined
      .map(src => (src || '').trim())
      .filter(src => {
        if (!src) return false;
        const lower = src.toLowerCase();
        if (lower.includes('via.placeholder.com')) return false;
        if (lower.includes('placeholder')) return false;
        const normalizedKey = normalizeImageSrc(src);
        if (!normalizedKey) return false;
        if (seen.has(normalizedKey)) return false;
        seen.add(normalizedKey);
        return true;
      })
      .slice(0, 8);
  }, [currentExperience]);

  const heroGallerySignature = React.useMemo(() => heroImageSources.join('|'), [heroImageSources]);

  useEffect(() => {
    if (!currentExperience) {
      setHeroFailedUrls({});
      setHeroImageIndex(0);
      return;
    }

    setHeroFailedUrls({});
    setHeroImageIndex(0);
  }, [currentExperience, currentExperience?.id, heroGallerySignature]);

  const heroValidUrls = React.useMemo(
    () => heroImageSources.filter(src => !heroFailedUrls[src]),
    [heroImageSources, heroFailedUrls],
  );

  useEffect(() => {
    if (heroValidUrls.length === 0) {
      if (heroImageIndex !== 0) {
        setHeroImageIndex(0);
      }
      return;
    }

    if (heroImageIndex >= heroValidUrls.length) {
      setHeroImageIndex(heroValidUrls.length - 1);
    }
  }, [heroValidUrls, heroImageIndex]);

  if (!currentExperience) {
    return null;
  }

  const activeHeroImage = heroValidUrls[heroImageIndex] ?? null;
  const categoryLabel = Array.isArray(currentExperience.category)
    ? currentExperience.category.join(' • ')
    : currentExperience.category;

  const activityId = currentExperience.id;
  const enrichedDetails = enrichedDetailsMap[activityId] || null;
  const isLoadingDetails = loadingMap[activityId] || false;
  const detailsError = errorMap[activityId] || null;

  const sanitizeGeneratedText = (raw?: string, jsonField?: keyof ActivityDetails): string | undefined => {
    if (!raw) return raw;

    const trimmed = raw.trim();
    const start = trimmed.indexOf('{');
    const end = trimmed.lastIndexOf('}');

    if (start !== -1 && end !== -1 && end > start) {
      const candidate = trimmed.slice(start, end + 1);
      try {
        const parsed = JSON.parse(candidate);
        if (jsonField && typeof parsed[jsonField] === 'string') {
          return parsed[jsonField].trim();
        }
      } catch (error) {
        log.warn('Failed to parse embedded JSON payload', error);
      }
    }

    let cleaned = trimmed
      .replace(/Here is the JSON requested:?/gi, '')
      .replace(/```json/gi, '')
      .replace(/```/g, '')
      .trim();

    const markerIndex = cleaned.toLowerCase().indexOf('here is the json requested');
    if (markerIndex > -1) {
      cleaned = cleaned.slice(0, markerIndex).trim();
    }

    cleaned = cleaned.replace(/^"/, '').replace(/"$/, '').trim();

    return cleaned || undefined;
  };

  const handleGetMoreDetails = async () => {
    if (!currentExperience) return;

    // Set loading state for this specific activity
    setLoadingMap(prev => ({ ...prev, [activityId]: true }));
    setErrorMap(prev => ({ ...prev, [activityId]: '' }));

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/activity-details`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          activityName: currentExperience.name,
          destinationName: destination || currentExperience.address || 'Unknown',
          locationName: currentExperience.extraDetails?.location_name || currentExperience.name,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch activity details');
      }

      const data: ActivityDetails = await response.json();

      // Store enriched details for this specific activity
      setEnrichedDetailsMap(prev => ({ ...prev, [activityId]: data }));
      setSuccessNotifications(prev => ({ ...prev, [activityId]: true }));

      // Auto-hide success notification after 4 seconds
      setTimeout(() => {
        setSuccessNotifications(prev => ({ ...prev, [activityId]: false }));
      }, 4000);
    } catch (error) {
      log.error('Error fetching activity details', error);
      setErrorMap(prev => ({ ...prev, [activityId]: 'Failed to load additional details. Please try again.' }));
    } finally {
      setLoadingMap(prev => ({ ...prev, [activityId]: false }));
    }
  };

  const cleanedDescription = sanitizeGeneratedText(currentExperience.description, 'description');
  const cleanedDuration = sanitizeGeneratedText(currentExperience.duration, 'duration');
  const cleanedCost = sanitizeGeneratedText(currentExperience.cost, 'cost');
  const cleanedWorkingHours = sanitizeGeneratedText(currentExperience.workingHours, 'working_hours');

  return (
    <>
      <Drawer
        anchor={isMobile ? 'bottom' : 'right'}
        open={open}
        onClose={onClose}
        sx={{
          zIndex: 1400, // Higher than fullscreen map (1300)
          '& .MuiBackdrop-root': {
            zIndex: 'auto', // Let MUI manage backdrop z-index (it will be below Paper automatically)
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          },
          '& .MuiPaper-root': {
            zIndex: 1401, // Ensure Paper is above backdrop
            boxShadow: isMobile
              ? '0 -4px 20px rgba(0, 0, 0, 0.15)' // Shadow on top for bottom drawer
              : '2px 0 8px rgba(0, 0, 0, 0.15)', // Shadow on left for right drawer
            // Force right positioning on desktop - override any transforms
            ...(!isMobile && {
              right: '0 !important',
              left: 'auto !important',
            }),
          },
          // Override the Slide transform for right anchor
          '& .MuiDrawer-paper': {
            ...(!isMobile && {
              right: 0,
              left: 'auto',
            }),
          },
        }}
        PaperProps={{
          sx: {
            width: { xs: '100%', md: 480 },
            height: { xs: '100dvh', md: '100%' }, // Use dvh (dynamic viewport height) for mobile to account for browser UI
            maxHeight: { xs: '100dvh', md: '100%' },
            borderRadius: { xs: '16px 16px 0 0', md: '8px 0 0 8px' },
            display: 'flex',
            flexDirection: 'column',
            // Ensure drawer is truly full screen on mobile
            ...(isMobile && {
              position: 'fixed',
              bottom: 0,
              top: 0,
              left: 0,
              right: 0,
            }),
            // Explicitly ensure right positioning on desktop
            ...(!isMobile && {
              right: 0,
              left: 'auto',
            }),
            margin: 0,
            padding: 0,
          } 
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box
            sx={{
              position: 'relative',
              minHeight: { xs: 230, md: 320 },
              overflow: 'hidden',
              background: activeHeroImage ? theme.palette.grey[900] : heroGradient,
            }}
          >
            {activeHeroImage && (
              <Box
                component="img"
                src={activeHeroImage}
                alt={`Photo of ${currentExperience.name}`}
                onError={() => {
                  setHeroFailedUrls(prev => ({ ...prev, [activeHeroImage]: true }));
                }}
                sx={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  zIndex: 0,
                }}
              />
            )}
            {!activeHeroImage && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  background: heroGradient,
                  zIndex: 0,
                }}
              />
            )}

            {heroValidUrls.length > 1 && (
              <>
                <IconButton
                  onClick={event => {
                    event.stopPropagation();
                    setHeroImageIndex(prev => {
                      if (heroValidUrls.length <= 1) return 0;
                      return prev === 0 ? heroValidUrls.length - 1 : prev - 1;
                    });
                  }}
                  sx={{
                    position: 'absolute',
                    left: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    bgcolor: alpha(theme.palette.common.white, 0.85),
                    '&:hover': {
                      bgcolor: theme.palette.common.white,
                    },
                    width: 36,
                    height: 36,
                    zIndex: 4,
                    boxShadow: '0 8px 22px rgba(15, 23, 42, 0.22)',
                  }}
                >
                  <KeyboardArrowLeftRoundedIcon fontSize="small" />
                </IconButton>

                <IconButton
                  onClick={event => {
                    event.stopPropagation();
                    setHeroImageIndex(prev => {
                      if (heroValidUrls.length <= 1) return 0;
                      return prev === heroValidUrls.length - 1 ? 0 : prev + 1;
                    });
                  }}
                  sx={{
                    position: 'absolute',
                    right: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    bgcolor: alpha(theme.palette.common.white, 0.85),
                    '&:hover': {
                      bgcolor: theme.palette.common.white,
                    },
                    width: 36,
                    height: 36,
                    zIndex: 4,
                    boxShadow: '0 8px 22px rgba(15, 23, 42, 0.22)',
                  }}
                >
                  <KeyboardArrowRightRoundedIcon fontSize="small" />
                </IconButton>

                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 14,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    bgcolor: alpha(theme.palette.common.black, 0.45),
                    color: '#FFFFFF',
                    borderRadius: '999px',
                    px: 1.5,
                    py: 0.4,
                    fontSize: textScale.support.fontSize,
                    fontWeight: 600,
                    letterSpacing: '0.05em',
                    zIndex: 2,
                    userSelect: 'none',
                  }}
                >
                  {heroImageIndex + 1} / {heroValidUrls.length}
                </Box>
              </>
            )}

            <Box
              sx={{
                position: 'relative',
                zIndex: 3,
                height: '100%',
                p: { xs: 3, md: 4 },
              }}
            >
              <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
                <IconButton
                  onClick={onClose}
                  sx={{
                    bgcolor: alpha(theme.palette.common.white, 0.85),
                    '&:hover': {
                      bgcolor: theme.palette.common.white,
                    },
                    width: 36,
                    height: 36,
                    boxShadow: '0 8px 22px rgba(15, 23, 42, 0.22)',
                  }}
                >
                  <CloseRoundedIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </Box>

        <Box sx={{ flexGrow: 1, p: { xs: 3, md: 4 }, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {categoryLabel && (
              <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: '0.08em' }}>
                {categoryLabel}
              </Typography>
            )}
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
              {currentExperience.name}
            </Typography>
          </Box>
          <Stack spacing={1}>
            {cleanedDescription && (
              <MarkdownText variant="body1" sx={{ color: 'text.secondary' }}>
                {emphasizeMarkdownLead(cleanedDescription)}
              </MarkdownText>
            )}
            {(cleanedDuration || cleanedCost || cleanedWorkingHours) && (
              <Stack spacing={1}>
                {cleanedDuration && (
                  <MarkdownText variant="body2">{emphasizeMarkdownLead(`⏱ ${cleanedDuration}`)}</MarkdownText>
                )}
                {cleanedCost && (
                  <MarkdownText variant="body2">{emphasizeMarkdownLead(`💰 ${cleanedCost}`)}</MarkdownText>
                )}
                {cleanedWorkingHours && (
                  <MarkdownText variant="body2">{emphasizeMarkdownLead(`🕒 ${cleanedWorkingHours}`)}</MarkdownText>
                )}
              </Stack>
            )}
            {currentExperience.address && (() => {
              // Build search query: "Activity Name, City" or "Activity Name, Destination"
              const activityName = currentExperience.name || '';
              const city = currentExperience.city || 
                          (currentExperience.extraDetails?.city as string) || 
                          (currentExperience.extraDetails?.location as string) ||
                          destination?.split(',')[0]?.trim() || 
                          destination?.trim() || 
                          '';
              
              // Construct search query: prefer "Name, City" format for better results
              let searchQuery = activityName;
              if (city && !activityName.toLowerCase().includes(city.toLowerCase())) {
                searchQuery = `${activityName}, ${city}`;
              } else if (!city && destination) {
                // Fallback to destination if no city
                const destinationCity = destination.split(',')[0]?.trim() || destination.trim();
                if (!activityName.toLowerCase().includes(destinationCity.toLowerCase())) {
                  searchQuery = `${activityName}, ${destinationCity}`;
                }
              }
              
              const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(searchQuery)}`;
              
              return (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
                  <MarkdownText variant="body2" sx={{ flex: 1, minWidth: 0 }}>{emphasizeMarkdownLead(`📍 ${currentExperience.address}`)}</MarkdownText>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<MapIcon sx={{ fontSize: '1rem' }} />}
                    endIcon={<OpenInNewIcon sx={{ fontSize: '0.875rem' }} />}
                    href={googleMapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      minWidth: 'auto',
                      px: 1.5,
                      py: 0.5,
                      fontSize: '0.75rem',
                      textTransform: 'none',
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      color: theme.palette.primary.main,
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        backgroundColor: alpha(theme.palette.primary.main, 0.04),
                      },
                    }}
                  >
                    View on Map
                  </Button>
                </Box>
              );
            })()}
          </Stack>

          {currentExperience.notes && (
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Insider tip
              </Typography>
              <MarkdownText variant="body2" sx={{ color: 'text.secondary' }}>
                {emphasizeMarkdownLead(currentExperience.notes)}
              </MarkdownText>
            </Box>
          )}

          {currentExperience.highlights && currentExperience.highlights.length > 0 && (
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                Highlights
              </Typography>
              <Stack spacing={1.25}>
                {currentExperience.highlights.map((highlight) => (
                  <Stack direction="row" spacing={1.5} key={highlight} alignItems="flex-start">
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: accentColor, mt: 0.75 }} />
                    <MarkdownText variant="body2" sx={{ color: 'text.secondary' }}>
                      {highlight}
                    </MarkdownText>
                  </Stack>
                ))}
              </Stack>
            </Box>
          )}

          {isLoadingDetails && !enrichedDetails && (
            <Box sx={{
              p: 3,
              backgroundColor: 'rgba(0, 166, 128, 0.05)',
              borderRadius: 3,
              border: '1px solid rgba(0, 166, 128, 0.2)',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              alignItems: 'center'
            }}>
              <CircularProgress size={40} sx={{ color: accentColor }} />
              <Box sx={{ width: '100%' }}>
                <LinearProgress sx={{
                  backgroundColor: 'rgba(0, 166, 128, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: accentColor
                  }
                }} />
              </Box>
              <Typography variant="body1" sx={{ fontWeight: 600, color: accentColor, textAlign: 'center' }}>
                Generating detailed insights...
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                Our AI is gathering historical context, cultural insights, and local tips for you. This may take a few moments.
              </Typography>
            </Box>
          )}

          {enrichedDetails && (
            <>
              <Divider sx={{ my: 3 }} />

              {enrichedDetails.historical_significance && (
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    Historical Significance
                  </Typography>
                  <MarkdownText variant="body2" sx={{ color: 'text.secondary' }}>
                    {emphasizeMarkdownLead(enrichedDetails.historical_significance)}
                  </MarkdownText>
                </Box>
              )}

              {enrichedDetails.cultural_context && (
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    Cultural Context
                  </Typography>
                  <MarkdownText variant="body2" sx={{ color: 'text.secondary' }}>
                    {emphasizeMarkdownLead(enrichedDetails.cultural_context)}
                  </MarkdownText>
                </Box>
              )}

              {enrichedDetails.visitor_experience && (
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    Visitor Experience
                  </Typography>
                  <MarkdownText variant="body2" sx={{ color: 'text.secondary' }}>
                    {emphasizeMarkdownLead(enrichedDetails.visitor_experience)}
                  </MarkdownText>
                </Box>
              )}

              {enrichedDetails.insider_tips && enrichedDetails.insider_tips.length > 0 && (
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                    Insider Tips
                  </Typography>
                  <Stack spacing={1.25}>
                    {enrichedDetails.insider_tips.map((tip, index) => (
                      <Stack direction="row" spacing={1.5} key={index} alignItems="flex-start">
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: accentColor, mt: 0.75 }} />
                        <MarkdownText variant="body2" sx={{ color: 'text.secondary' }}>
                          {emphasizeMarkdownLead(tip)}
                        </MarkdownText>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              )}

              {enrichedDetails.seasonal_considerations && (
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    Seasonal Considerations
                  </Typography>
                  <MarkdownText variant="body2" sx={{ color: 'text.secondary' }}>
                    {emphasizeMarkdownLead(enrichedDetails.seasonal_considerations)}
                  </MarkdownText>
                </Box>
              )}

              {enrichedDetails.interesting_facts && enrichedDetails.interesting_facts.length > 0 && (
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                    Interesting Facts
                  </Typography>
                  <Stack spacing={1.25}>
                    {enrichedDetails.interesting_facts.map((fact, index) => (
                      <Stack direction="row" spacing={1.5} key={index} alignItems="flex-start">
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: accentColor, mt: 0.75 }} />
                        <MarkdownText variant="body2" sx={{ color: 'text.secondary' }}>
                          {emphasizeMarkdownLead(fact)}
                        </MarkdownText>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              )}

              {enrichedDetails.local_perspective && (
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    Local Perspective
                  </Typography>
                  <MarkdownText variant="body2" sx={{ color: 'text.secondary' }}>
                    {emphasizeMarkdownLead(enrichedDetails.local_perspective)}
                  </MarkdownText>
                </Box>
              )}
            </>
          )}

          {detailsError && (
            <Box sx={{ p: 2, backgroundColor: '#fff3cd', borderRadius: 2, border: '1px solid #ffeaa7' }}>
              <Typography variant="body2" sx={{ color: '#856404' }}>
                {detailsError}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 'auto' }}>
            {!enrichedDetails && !isLoadingDetails && (
              <Button
                variant="outlined"
                color="primary"
                size="large"
                fullWidth
                onClick={handleGetMoreDetails}
              >
                Get more details
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              onClick={onClose}
            >
              Back to Plan
            </Button>
            {onDelete && (
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={() => {
                  onDelete(currentExperience.id);
                  onClose();
                }}
                disabled={isDeleting}
                sx={{
                  bgcolor: '#e74c3c',
                  color: 'white',
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: '#c0392b'
                  },
                  '&:disabled': {
                    bgcolor: 'rgba(231, 76, 60, 0.5)',
                    color: 'rgba(255, 255, 255, 0.7)'
                  }
                }}
              >
                {isDeleting ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                    Deleting...
                  </>
                ) : (
                  'Delete Activity'
                )}
              </Button>
            )}
          </Box>
        </Box>
      </Box>
      </Drawer>

      {/* Loading notifications - one for each loading activity */}
      {Object.entries(loadingMap).map(([id, isLoading]) => {
        if (!isLoading) return null;
        const loadingExperience = experienceMap[id];
        if (!loadingExperience) return null;

        return (
          <Box
            key={`loading-${id}`}
            sx={{
              position: 'fixed',
              top: { xs: 70, sm: 80 },
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 10000,
              animation: 'slideDown 0.3s ease-out',
              '@keyframes slideDown': {
                from: {
                  opacity: 0,
                  transform: 'translateX(-50%) translateY(-20px)',
                },
                to: {
                  opacity: 1,
                  transform: 'translateX(-50%) translateY(0)',
                },
              },
            }}
          >
            <Box
              sx={{
                backgroundColor: alpha('#FFFFFF', 0.98),
                backdropFilter: 'blur(10px)',
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                boxShadow: `0 4px 12px ${alpha('#000000', 0.08)}, 0 0 0 1px ${alpha(theme.palette.primary.main, 0.05)}`,
                overflow: 'hidden',
                minWidth: { xs: 320, sm: 400 },
                maxWidth: { xs: '90vw', sm: 500 },
              }}
            >
              {/* Thin horizontal progress bar at the top */}
              <Box
                sx={{
                  width: '100%',
                  height: 3,
                  backgroundColor: alpha(theme.palette.primary.main, 0.12),
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    bottom: 0,
                    width: '50%',
                    background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}, transparent)`,
                    animation: 'slideRight 1.8s ease-in-out infinite',
                    '@keyframes slideRight': {
                      '0%': {
                        transform: 'translateX(-100%)',
                      },
                      '100%': {
                        transform: 'translateX(300%)',
                      },
                    },
                  }}
                />
              </Box>

              {/* Content area */}
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                {/* Text content */}
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 500,
                    fontSize: textScale.support.fontSize,
                    letterSpacing: '0.01em',
                    flex: 1,
                  }}
                >
                  Searching for activity details...
                </Typography>

                {/* Small circular progress indicator */}
                <CircularProgress
                  size={14}
                  thickness={5}
                  sx={{
                    color: theme.palette.primary.main,
                  }}
                />
              </Box>
            </Box>
          </Box>
        );
      })}

      {/* Success notifications - one for each completed activity */}
      {Object.entries(successNotifications).map(([id, show]) => {
        if (!show) return null;
        const successExperience = experienceMap[id];
        if (!successExperience) return null;

        return (
          <Box
            key={`success-${id}`}
            sx={{
              position: 'fixed',
              top: { xs: 70, sm: 80 },
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 10000,
              animation: 'slideDown 0.3s ease-out',
              '@keyframes slideDown': {
                from: {
                  opacity: 0,
                  transform: 'translateX(-50%) translateY(-20px)',
                },
                to: {
                  opacity: 1,
                  transform: 'translateX(-50%) translateY(0)',
                },
              },
            }}
          >
            <Box
              sx={{
                backgroundColor: alpha(accentColor, 0.98),
                backdropFilter: 'blur(10px)',
                borderRadius: 2,
                border: `1px solid ${alpha(accentColor, 0.3)}`,
                boxShadow: `0 4px 12px ${alpha(accentColor, 0.25)}`,
                overflow: 'hidden',
                minWidth: { xs: 320, sm: 400 },
                maxWidth: { xs: '90vw', sm: 500 },
                cursor: 'pointer',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 6px 16px ${alpha(accentColor, 0.35)}`,
                },
              }}
              onClick={() => {
                // Dismiss the notification
                setSuccessNotifications(prev => ({ ...prev, [id]: false }));
                // Open the activity card
                if (onOpenExperience && successExperience) {
                  onOpenExperience(successExperience);
                }
              }}
            >
              {/* Success indicator bar at the top */}
              <Box
                sx={{
                  width: '100%',
                  height: 3,
                  backgroundColor: alpha('#FFFFFF', 0.4),
                }}
              />

              {/* Content area */}
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                {/* Success icon */}
                <CheckCircleIcon sx={{ fontSize: 16, color: '#FFFFFF' }} />
                
                {/* Text content */}
                <Typography
                  variant="caption"
                  sx={{
                    color: '#FFFFFF',
                    fontWeight: 500,
                    fontSize: textScale.support.fontSize,
                    letterSpacing: '0.01em',
                    flex: 1,
                  }}
                >
                  Details loaded! Click to view {successExperience.name}
                </Typography>
              </Box>
            </Box>
          </Box>
        );
      })}
    </>
  );
};
